export const CATEGORY_FIELDS = ['xaxis', 'date', 'category', 'year'];
export const SERIES_FIELDS = ['serie', 'series', 'type', 'label', 'name'];
export const SERIES_GROUP_FIELDS = ['serie_group', 'series_group', 'serieGroup', 'seriesGroup'];
export const VALUE_FIELDS = ['value', 'count', 'total', 'qty', 'amount'];
export const BAR_SERIE_GROUP = 1;
export const LINE_SERIE_GROUP = 2;

const IGNORED_SERIES_FIELDS = new Set(['xaxis', ...CATEGORY_FIELDS, ...SERIES_GROUP_FIELDS]);

const isPresent = (value) => value !== undefined && value !== null && value !== '';

export const parseNumber = (value) => {
    if (typeof value === 'number') {
        return value;
    }

    if (typeof value === 'string') {
        return Number(value.trim().replace(/\s/g, '').replace(',', '.'));
    }

    return Number(value);
};

export const getFirstValue = (item, fields) => fields
    .map((field) => item?.[field])
    .find(isPresent);

export const isLongFormatData = (data) => Array.isArray(data) && data.some((item) => (
    isPresent(getFirstValue(item, SERIES_FIELDS))
    && isPresent(getFirstValue(item, VALUE_FIELDS))
));

const normalizeLongFormatData = (data) => {
    const rowsByCategory = new Map();

    data.forEach((item) => {
        const category = getFirstValue(item, CATEGORY_FIELDS);
        const seriesName = getFirstValue(item, SERIES_FIELDS);
        const value = parseNumber(getFirstValue(item, VALUE_FIELDS));

        if (!isPresent(category) || !isPresent(seriesName) || !Number.isFinite(value)) {
            return;
        }

        const categoryKey = String(category);
        const seriesKey = String(seriesName);
        const currentRow = rowsByCategory.get(categoryKey) || { xaxis: categoryKey };
        currentRow[seriesKey] = (currentRow[seriesKey] || 0) + value;
        rowsByCategory.set(categoryKey, currentRow);
    });

    return Array.from(rowsByCategory.values());
};

const normalizeWideFormatData = (data) => data.map((item) => {
    const category = getFirstValue(item, CATEGORY_FIELDS);
    const normalizedItem = {
        ...item,
        xaxis: isPresent(category) ? String(category) : item?.xaxis
    };

    CATEGORY_FIELDS
        .filter((field) => field !== 'xaxis')
        .forEach((field) => {
            delete normalizedItem[field];
        });

    Object.keys(normalizedItem).forEach((field) => {
        if (field === 'xaxis') {
            return;
        }

        const numericValue = parseNumber(normalizedItem[field]);
        if (Number.isFinite(numericValue)) {
            normalizedItem[field] = numericValue;
        }
    });

    return normalizedItem;
});

export const normalizeSeriesData = (data) => {
    if (!Array.isArray(data)) {
        return [];
    }

    return isLongFormatData(data)
        ? normalizeLongFormatData(data)
        : normalizeWideFormatData(data);
};

export const getSeriesNames = (data) => {
    if (!Array.isArray(data) || data.length === 0) {
        return [];
    }

    const names = new Set();

    data.forEach((item) => {
        Object.keys(item || {}).forEach((field) => {
            if (IGNORED_SERIES_FIELDS.has(field)) {
                return;
            }

            if (data.some((row) => Number.isFinite(parseNumber(row?.[field])))) {
                names.add(field);
            }
        });
    });

    return Array.from(names);
};

export const getSeriesGroups = (data) => {
    const groupsBySeries = new Map();

    if (!Array.isArray(data)) {
        return groupsBySeries;
    }

    data.forEach((item) => {
        const seriesName = getFirstValue(item, SERIES_FIELDS);
        const groupValue = getFirstValue(item, SERIES_GROUP_FIELDS);

        if (!isPresent(seriesName) || !isPresent(groupValue)) {
            return;
        }

        const group = parseNumber(groupValue);
        const seriesKey = String(seriesName);

        if (Number.isFinite(group) && !groupsBySeries.has(seriesKey)) {
            groupsBySeries.set(seriesKey, group);
        }
    });

    return groupsBySeries;
};

const hasCategoryValue = (data) => data.some((item) => (
    CATEGORY_FIELDS.some((field) => isPresent(item?.[field]))
));

const normalizeLongPieData = (data) => {
    const seriesNames = Array.from(new Set(
        data
            .map((item) => getFirstValue(item, SERIES_FIELDS))
            .filter(isPresent)
            .map(String)
    ));
    const useSeriesAsCategory = seriesNames.length > 1;
    const valuesByCategory = new Map();

    data.forEach((item) => {
        const seriesName = getFirstValue(item, SERIES_FIELDS);
        const fallbackCategory = getFirstValue(item, CATEGORY_FIELDS);
        const category = useSeriesAsCategory ? seriesName : fallbackCategory;
        const value = parseNumber(getFirstValue(item, VALUE_FIELDS));

        if (!isPresent(category) || !Number.isFinite(value) || value <= 0) {
            return;
        }

        const categoryKey = String(category);
        valuesByCategory.set(categoryKey, (valuesByCategory.get(categoryKey) || 0) + value);
    });

    return Array.from(valuesByCategory.entries()).map(([category, value]) => ({ category, value }));
};

const normalizeWidePieData = (data) => {
    const normalizedData = normalizeWideFormatData(data);
    const valueFields = getSeriesNames(normalizedData);

    if (valueFields.length === 0) {
        return [];
    }

    if (!hasCategoryValue(data) && normalizedData.length === 1 && valueFields.length > 1) {
        return valueFields
            .map((field) => ({
                category: field,
                value: parseNumber(normalizedData[0]?.[field])
            }))
            .filter((item) => Number.isFinite(item.value) && item.value > 0);
    }

    const valueField = valueFields[0];

    return normalizedData
        .map((item) => ({
            category: isPresent(item?.xaxis) ? String(item.xaxis) : valueField,
            value: parseNumber(item?.[valueField])
        }))
        .filter((item) => Number.isFinite(item.value) && item.value > 0);
};

export const normalizePieData = (data) => {
    if (!Array.isArray(data)) {
        return [];
    }

    return isLongFormatData(data)
        ? normalizeLongPieData(data)
        : normalizeWidePieData(data);
};
